import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Camera, Loader2, Save, MapPin, Languages, Banknote,
  Calendar, Award, X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGES = [
  { key: "fr", label: "Français" },
  { key: "wo", label: "Wolof" },
  { key: "en", label: "English" },
  { key: "ar", label: "العربية" },
];

export default function ArtisanProfileEdit({ user, onUserUpdate }) {
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const [specialties, setSpecialties] = useState([]);
  const [form, setForm] = useState({
    user_id: user?.id || "",
    specialties: [],
    bio: "",
    hourly_rate: 0,
    daily_rate: 0,
    years_experience: 0,
    portfolio_urls: [],
    languages: ["fr", "wo"],
    service_radius_km: 15,
    base_latitude: null,
    base_longitude: null,
  });
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [spec, prof] = await Promise.all([
        axios.get(`${API}/artisans/specialties`),
        axios.get(`${API}/artisans/profile/${user.id}`).catch(() => ({ data: null })),
      ]);
      setSpecialties(spec.data || []);
      if (prof?.data) {
        setForm((f) => ({
          ...f,
          specialties: prof.data.specialties || [],
          bio: prof.data.bio || "",
          hourly_rate: prof.data.hourly_rate || 0,
          daily_rate: prof.data.daily_rate || 0,
          years_experience: prof.data.years_experience || 0,
          portfolio_urls: prof.data.portfolio_urls || [],
          languages: prof.data.languages || ["fr", "wo"],
          service_radius_km: prof.data.service_radius_km || 15,
          base_latitude: prof.data.base_latitude || null,
          base_longitude: prof.data.base_longitude || null,
        }));
        if (prof.data.avatar_url) setAvatarUrl(prof.data.avatar_url);
      }
    } catch (err) {
      console.error("Load profile error:", err);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSpecialty = (key) => {
    setForm((f) => ({
      ...f,
      specialties: f.specialties.includes(key)
        ? f.specialties.filter((s) => s !== key)
        : [...f.specialties, key],
    }));
  };

  const toggleLanguage = (key) => {
    setForm((f) => ({
      ...f,
      languages: f.languages.includes(key)
        ? f.languages.filter((l) => l !== key)
        : [...f.languages, key],
    }));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation non supportée");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, base_latitude: pos.coords.latitude, base_longitude: pos.coords.longitude }));
        toast.success("Position enregistrée");
      },
      () => toast.error("Position refusée par votre navigateur"),
    );
  };

  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("user_id", user.id);
      const r = await axios.post(`${API}/artisans/upload-avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAvatarUrl(r.data.avatar_url);
      toast.success("Photo de profil mise à jour");
      onUserUpdate?.({ ...user, avatar_url: r.data.avatar_url });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAvatar = async () => {
    try {
      await axios.patch(`${API}/users/${user.id}`, { avatar_url: null }).catch(() => {});
      setAvatarUrl(null);
      onUserUpdate?.({ ...user, avatar_url: null });
      toast.success("Photo retirée");
    } catch {
      toast.error("Erreur");
    }
  };

  const save = async () => {
    if (form.specialties.length === 0) {
      toast.error("Sélectionnez au moins une spécialité");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/artisans/profile`, form);
      toast.success("Profil sauvegardé");
      navigate("/artisans/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-32" data-testid="artisan-profile-edit-page">
      {/* HEADER */}
      <header className="bg-[#1E293B]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-40 px-5 py-4 flex justify-between items-center">
        <button
          onClick={() => navigate("/artisans/dashboard")}
          className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:text-white"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-sm font-black italic tracking-tight uppercase">
          <span className="text-[#D4AF37]">Mon</span> profil
        </h1>
        <div className="w-9" />
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 space-y-6">
        {/* ============ AVATAR ============ */}
        <section className="bg-[#1E293B] border border-white/5 rounded-3xl p-6 flex flex-col items-center gap-4" data-testid="avatar-section">
          <div className="relative">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Avatar"
                className="w-24 h-24 rounded-3xl object-cover ring-2 ring-[#D4AF37]/40"
                data-testid="current-avatar"
              />
            ) : (
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center text-[#0F172A] font-black text-3xl">
                {(user?.full_name || "A").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/60 rounded-3xl flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-white" />
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onAvatarChange}
            className="hidden"
            data-testid="avatar-file-input"
          />
          <div className="flex gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#0F172A] rounded-full font-black text-xs uppercase tracking-widest"
              data-testid="upload-avatar-btn"
            >
              <Camera className="w-4 h-4 mr-2" /> {avatarUrl ? "Changer" : "Ajouter une photo"}
            </Button>
            {avatarUrl && (
              <Button
                onClick={removeAvatar}
                variant="outline"
                className="rounded-full border-white/20 text-slate-300 hover:bg-white/5 text-xs"
                data-testid="remove-avatar-btn"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </section>

        {/* ============ SPECIALTIES ============ */}
        <section className="bg-[#1E293B] border border-white/5 rounded-3xl p-6">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
            <Award className="w-3.5 h-3.5" /> Spécialités
          </h3>
          <div className="flex flex-wrap gap-2" data-testid="specialties-grid">
            {specialties.map((s) => {
              const active = form.specialties.includes(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggleSpecialty(s.key)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                    active
                      ? "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]"
                      : "bg-white/5 text-slate-300 border-white/10 hover:border-white/30"
                  }`}
                  data-testid={`spec-${s.key}`}
                >
                  <span className="mr-1.5">{s.icon}</span> {s.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* ============ BIO ============ */}
        <section className="bg-[#1E293B] border border-white/5 rounded-3xl p-6">
          <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 block">
            Présentation
          </Label>
          <Textarea
            rows={4}
            maxLength={600}
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            placeholder="Décrivez votre savoir-faire, votre expérience, vos engagements..."
            className="bg-[#0F172A] border-white/10 text-white placeholder:text-slate-600"
            data-testid="bio-input"
          />
          <p className="text-[9px] text-slate-500 mt-1 text-right">{form.bio.length}/600</p>
        </section>

        {/* ============ TARIFS + EXPÉRIENCE ============ */}
        <section className="bg-[#1E293B] border border-white/5 rounded-3xl p-6 space-y-4">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <Banknote className="w-3.5 h-3.5" /> Tarifs & Expérience
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[9px] uppercase tracking-widest text-slate-500 mb-1.5 block">
                Taux horaire (FCFA)
              </Label>
              <Input
                type="number"
                min="0"
                value={form.hourly_rate}
                onChange={(e) => setForm({ ...form, hourly_rate: parseInt(e.target.value, 10) || 0 })}
                className="bg-[#0F172A] border-white/10 text-white"
                data-testid="hourly-rate-input"
              />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-widest text-slate-500 mb-1.5 block">
                Tarif jour (FCFA)
              </Label>
              <Input
                type="number"
                min="0"
                value={form.daily_rate}
                onChange={(e) => setForm({ ...form, daily_rate: parseInt(e.target.value, 10) || 0 })}
                className="bg-[#0F172A] border-white/10 text-white"
                data-testid="daily-rate-input"
              />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-widest text-slate-500 mb-1.5 block flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Années d'expérience
              </Label>
              <Input
                type="number"
                min="0"
                max="60"
                value={form.years_experience}
                onChange={(e) => setForm({ ...form, years_experience: parseInt(e.target.value, 10) || 0 })}
                className="bg-[#0F172A] border-white/10 text-white"
                data-testid="years-input"
              />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-widest text-slate-500 mb-1.5 block">
                Rayon d'intervention (km)
              </Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={form.service_radius_km}
                onChange={(e) => setForm({ ...form, service_radius_km: parseInt(e.target.value, 10) || 15 })}
                className="bg-[#0F172A] border-white/10 text-white"
                data-testid="radius-input"
              />
            </div>
          </div>
        </section>

        {/* ============ LANGUES ============ */}
        <section className="bg-[#1E293B] border border-white/5 rounded-3xl p-6">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
            <Languages className="w-3.5 h-3.5" /> Langues parlées
          </h3>
          <div className="flex flex-wrap gap-2" data-testid="languages-grid">
            {LANGUAGES.map((l) => {
              const active = form.languages.includes(l.key);
              return (
                <button
                  key={l.key}
                  type="button"
                  onClick={() => toggleLanguage(l.key)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                    active
                      ? "bg-[#003594]/20 text-blue-300 border-[#003594]"
                      : "bg-white/5 text-slate-300 border-white/10 hover:border-white/30"
                  }`}
                  data-testid={`lang-${l.key}`}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* ============ LOCATION ============ */}
        <section className="bg-[#1E293B] border border-white/5 rounded-3xl p-6">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> Zone de référence
          </h3>
          {form.base_latitude && form.base_longitude ? (
            <p className="text-xs text-emerald-300 mb-3" data-testid="location-display">
              📍 {form.base_latitude.toFixed(4)}, {form.base_longitude.toFixed(4)}
            </p>
          ) : (
            <p className="text-xs text-slate-500 italic mb-3">Aucune position enregistrée</p>
          )}
          <Button
            onClick={useMyLocation}
            variant="outline"
            className="rounded-full border-white/20 text-slate-200 hover:bg-white/5 text-xs"
            data-testid="use-location-btn"
          >
            <MapPin className="w-3.5 h-3.5 mr-2" /> Utiliser ma position actuelle
          </Button>
        </section>
      </main>

      {/* ============ STICKY SAVE ============ */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A] to-transparent p-5 z-30">
        <div className="max-w-2xl mx-auto">
          <Button
            onClick={save}
            disabled={saving}
            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 disabled:opacity-50 text-[#0F172A] py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-[#D4AF37]/20 flex items-center justify-center gap-2"
            data-testid="save-profile-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder mon profil
          </Button>
        </div>
      </div>
    </div>
  );
}
